import React, { FormEvent, useState } from 'react';
import { Button } from 'primereact/button';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useMutation } from '@tanstack/react-query';

import { InputTextField } from '@/components';
import { useContainerExtractedValidations } from '@/hooks';
import useToastNotification from '@/hooks/useToastNotification';
import { verifyRut } from '@/pages/CustomerRegistration/verifyRut';
import { generateANPExport } from '../generateANPExport';

import styles from './ANPImport.module.scss';

export interface ANPExportFormState {
  container: string;
  dispatcherTaxId: string;
}

interface ANPExportFormErrors {
  container: string;
  dispatcherTaxId: string;
}

const ANPExport: React.FC = () => {
  const { t } = useTranslation('workOrder');
  const { showToast } = useToastNotification();

  const [formData, setFormData] = useState<ANPExportFormState>({
    container: '',
    dispatcherTaxId: '',
  });

  const [formErrors, setFormErrors] = useState<ANPExportFormErrors>({
    container: '',
    dispatcherTaxId: '',
  });

  const { marca, numero, containerIsValid } = useContainerExtractedValidations(
    formData.container
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    const sanitizedValue =
      name === 'container' ? value.replace(/\//g, '') : value;
    setFormData((prevState) => ({
      ...prevState,
      [name]: sanitizedValue,
    }));
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  const validateForm = () => {
    let errors: ANPExportFormErrors = { ...formErrors };
    let isValid = true;

    if (!formData.container || !containerIsValid) {
      errors = {
        ...errors,
        container:
          formData.container && !containerIsValid
            ? t('containerInvalid')
            : t('containerRequired'),
      };
      isValid = false;
    }
    const dispatcherTaxIdRegex = /^\d{12}$/;
    if (!formData.dispatcherTaxId) {
      errors.dispatcherTaxId = t('dispatcherTaxIdRequired');
      isValid = false;
    } else if (!dispatcherTaxIdRegex.test(formData.dispatcherTaxId)) {
      errors.dispatcherTaxId = t('dispatcherTaxIdInvalid');
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const verifyRutMutation = useMutation({
    mutationFn: verifyRut,
    onError: (error) => {
      showToast({ severity: 'error', detail: `${error}` });
    },
  });

  const generateANPExportMutation = useMutation({
    mutationFn: generateANPExport,
    onError: (error) => {
      if (error.toString().trim() !== 'Error') {
        showToast({ severity: 'error', detail: `${error}` });
      } else {
        showToast({
          severity: 'error',
          detail: t('eirFormValidations.unexpectedError'),
        });
      }
    },
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validateForm()) {
      const payload = {
        dispatcherTaxId: formData.dispatcherTaxId,
        marca,
        numero,
      };

      try {
        const rutData = await verifyRutMutation.mutateAsync({
          rut: payload.dispatcherTaxId,
        });

        if (rutData?.m_Item1 === null && rutData?.m_Item2) {
          showToast({
            severity: 'error',
            detail: rutData?.m_Item2,
          });
        } else {
          generateANPExportMutation.mutate(payload);
        }
      } catch (error) {
        /* empty */
      }
    }
  };

  return (
    <div className={styles.container}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className="col-12 flex-column md:flex-row flex align-items-start  md:align-items-center pl-0">
          <InputTextField
            className={`col-12 md:col-5 pl-0 ${styles.inputText}`}
            label={t('container')}
            id="container"
            onChange={handleInputChange}
            value={formData.container}
            name="container"
            error={formErrors.container}
            placeholder={t('containerExample')}
            longErrorMessage
          />

          <InputTextField
            className={`col-12 md:col-5 pl-0 ${styles.inputText}`}
            label={t('dispatcherTaxId')}
            id="dispatcherTaxId"
            onChange={handleInputChange}
            value={formData.dispatcherTaxId}
            name="dispatcherTaxId"
            error={formErrors.dispatcherTaxId}
            placeholder={t('dispatcherTaxIdPlaceholder')}
          />

          <Button
            label={t('getOrderButton')}
            type="submit"
            className={classnames(styles.buttonOrder)}
            disabled={
              verifyRutMutation.isPending || generateANPExportMutation.isPending
            }
            loading={
              verifyRutMutation.isPending || generateANPExportMutation.isPending
            }
          />
        </div>
      </form>
      {generateANPExportMutation.data &&
        generateANPExportMutation.data.value && (
          <div
            className={classnames(
              styles.orderConfirmation,
              generateANPExportMutation.data.value.ok === false &&
                styles.errorBg
            )}
          >
            <FontAwesomeIcon
              icon={generateANPExportMutation.data.value.ok ? faCheck : faTimes}
              size="xl"
              className={styles.iconCheck}
            />
            <div>
              <strong>{`${generateANPExportMutation.data.value.mensaje}`}</strong>
            </div>
          </div>
        )}
    </div>
  );
};
export default ANPExport;
